/**
 * AuditeePortal — the public, auditee-facing page for one audit. No Core
 * session; auth is a mailed grant link + email OTP (the universe-portal
 * pattern, backed by /api/audit-portal).
 *
 * GOVERNING CONSTRAINT: the auditee sees only what they owe and what was
 * formally sent to them. Plain human language throughout — no auditor
 * vocabulary anywhere on this page.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PortalDashboard from './PortalDashboard.jsx';

const API = process.env.REACT_APP_API_URL || '';

function tokenFromUrl() {
  const m = window.location.pathname.match(/\/audit-portal\/([^/?#]+)/);
  return m ? m[1] : '';
}

/** Token-header fetch adapter for the shared request list. */
export function portalAdapter(token, pass) {
  const authHeaders = { Authorization: `Bearer ${token}`, 'x-mfa-pass': pass };
  return {
    respond: async (rid, text) => {
      const res = await fetch(`${API}/api/audit-portal/requests/${rid}/respond`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ response_text: text }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Your answer did not go through. Try again.');
      return d.request;
    },
    upload: async (rid, file) => {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/audit-portal/requests/${rid}/upload`, { method: 'POST', headers: authHeaders, body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'The upload did not go through. Try again.');
      return d.document;
    },
  };
}

export default function AuditeePortal() {
  const token = useMemo(tokenFromUrl, []);
  const [stage, setStage] = useState('loading'); // loading | dead | code | dashboard
  const [grant, setGrant] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [pass, setPass] = useState(null);
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const sendCode = async () => {
    setErr('');
    try {
      const res = await fetch(`${API}/api/audit-portal/mfa/challenge`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'We could not send the code. Try again in a minute.');
      setChallenge(d.challenge); setCode(''); setStage('code');
    } catch (e) { setErr(e.message); setStage('code'); }
  };

  useEffect(() => {
    if (!token) { setStage('dead'); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/api/audit-portal/verify`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        const d = await res.json();
        if (!res.ok) { setStage('dead'); return; }
        setGrant(d.grant);
        await sendCode();
      } catch { setStage('dead'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadView = async (p) => {
    const res = await fetch(`${API}/api/audit-portal/me`, { headers: { Authorization: `Bearer ${token}`, 'x-mfa-pass': p } });
    if (res.status === 401) { setNote('Your session timed out. We sent you a new code.'); await sendCode(); return false; }
    const d = await res.json();
    if (!res.ok) throw new Error(d.error || 'We could not open your page. Try again.');
    setView(d);
    return true;
  };

  const verifyCode = async () => {
    setErr('');
    try {
      const res = await fetch(`${API}/api/audit-portal/mfa/verify`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challenge?.challenge_id, code }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'That code did not match. Try again.');
      setPass(d.pass_token); setNote('');
      if (await loadView(d.pass_token)) setStage('dashboard');
    } catch (e) { setErr(e.message); }
  };

  const adapter = useMemo(() => (pass ? portalAdapter(token, pass) : null), [token, pass]);

  if (stage === 'loading') return <Shell><div className="text-sm text-slate-400 py-10 text-center">Opening your secure page…</div></Shell>;

  if (stage === 'dead') return (
    <Shell>
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="bg-white border border-slate-200 rounded-xl px-8 py-7 max-w-md w-full shadow-sm text-center">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Link not active</div>
          <h2 className="text-lg font-semibold text-slate-800 mb-1">This link is no longer active.</h2>
          <p className="text-sm text-slate-500">Contact your auditor.</p>
        </div>
      </div>
    </Shell>
  );

  if (stage === 'code') return (
    <Shell>
      <CodeCard
        masked={challenge?.recipient_masked || grant?.contact_email_masked || 'your email'}
        code={code} setCode={setCode} onVerify={verifyCode} onResend={sendCode} err={err} note={note}
      />
    </Shell>
  );

  return (
    <Shell>
      {view && adapter && (
        <PortalDashboard view={view} adapter={adapter} onChanged={() => loadView(pass)} />
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <span className="text-sm font-bold text-slate-800">Audit Portal</span>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 flex items-center gap-3">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Encrypted</span>
            <span>·</span><span>Verified email</span>
            <span>·</span><span>Recorded</span>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
}

function CodeCard({ masked, code, setCode, onVerify, onResend, err, note }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Confirm it's you</div>
      <h2 className="text-base font-semibold text-slate-800 mb-1">Enter your code</h2>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">
        We sent a 6-digit code to <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{masked}</span>. It expires in a few minutes.
      </p>
      {note && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">{note}</div>}
      <input
        ref={inputRef} type="text" inputMode="numeric" maxLength={6} value={code} aria-label="6-digit code"
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) onVerify(); }}
        placeholder="000000"
        className="w-52 border border-slate-300 focus:border-orange-500 outline-none rounded-lg px-4 py-3 text-2xl font-mono tracking-[0.4em] mb-4"
      />
      <div className="flex items-center gap-3">
        <button type="button" onClick={onVerify} disabled={code.length !== 6} className="px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">Open my page</button>
        <button type="button" onClick={onResend} className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 uppercase tracking-wide">Send a new code</button>
      </div>
      {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
    </div>
  );
}
